<template>
    <div class="reconciliation-match-card" :class="cardClasses">
        <div v-if="isPending" class="reconciliation-match-card-empty">
            <div class="reconciliation-row-icon-btn" title="Pesquisar" @click="onSearch">
                <i class="fa-solid fa-magnifying-glass"></i>
            </div>
            <div class="reconciliation-row-icon-btn" title="Criar lançamento" @click="onCreate">
                <i class="fa-solid fa-plus"></i>
            </div>
        </div>

        <template v-else-if="recordItem">
            <template v-if="!condensed">
                <div class="reconciliation-match-card-header">
                    <div class="reconciliation-match-card-value"
                        :class="{ 'reconciliation-match-card-value-negative': recordItem.value < 0, 'reconciliation-match-card-value-positive': recordItem.value >= 0 }">
                        {{ recordItem.value | currency }}
                    </div>
                    <div class="reconciliation-match-card-count">1 lançamento</div>
                </div>

                <div class="reconciliation-match-card-body">
                    <div class="reconciliation-match-card-description">
                        {{ recordItem.recipientName || recordItem.description }}
                    </div>
                    <div class="reconciliation-match-card-subtext" v-if="recordItem.recipientName">
                        {{ recordItem.description }} <span v-if="recordItem.dueDate">- {{ recordItem.dueDate }}</span>
                    </div>
                    <div class="reconciliation-match-card-existing-balance" v-if="hasExistingBalance">
                        Já possui baixa parcial de {{ existingPaidAmount | currency }} · Saldo atual:
                        {{ recordItem.baseRemainingValue | currency }}
                    </div>
                    <div class="reconciliation-match-card-paired-info" v-if="hasOtherPairedItems">
                        Pareado:
                        <span
                            :class="recordItem.value < 0 ? 'reconciliation-match-card-value-negative' : 'reconciliation-match-card-value-positive'">
                            {{ (recordItem.value < 0 ? -recordItem.pairedTotal : recordItem.pairedTotal) | currency }}
                                </span>
                                de {{ recordItem.baseRemainingValue | currency }}
                                <span v-if="recordItem.remainingValue > valueTolerance">
                                    (saldo restante:
                                    <span
                                        :class="recordItem.value < 0 ? 'reconciliation-match-card-value-negative' : 'reconciliation-match-card-value-positive'">
                                        {{ (recordItem.value < 0 ? -recordItem.remainingValue :
                                            recordItem.remainingValue) | currency }} </span>)
                                    </span>

                    </div>
                </div>
            </template>
            <div class="reconciliation-match-card-condensed-label" v-else>
                <i class="fa-solid fa-link"></i> Mesmo lançamento cadastrado
            </div>

            <div class="reconciliation-match-card-body">
                <div class="reconciliation-match-card-confidence" v-if="isSuggested">
                    {{ statementItem.matchedSuggestion ? ('Sugestão automática · ' + statementItem.confidenceText) :
                        'Vinculado manualmente' }}
                </div>
                <div class="reconciliation-match-card-diff" v-if="isDivergent && !hasOtherPairedItems">
                    {{ statementItem.diffText }}
                </div>

                <Badge v-if="isReconciled" title="Conciliado" type="success" size="small"
                    classIcon="fa-solid fa-circle-check" />
            </div>

            <div class="reconciliation-match-card-actions" v-if="isSuggested">
                <Button v-if="!condensed" :_key="'btnConciliar' + statementItem.id" title="Conciliar" size="small"
                    type="primary" :clicked="onConciliar" />
                <Button :_key="'btnDesfazer' + statementItem.id" title="Desfazer" size="small" type="danger"
                    :clicked="onDesfazer" />
            </div>

            <template v-if="isDivergent">
                <div class="reconciliation-match-card-actions">
                    <Button v-if="!condensed" :_key="'btnForceReconcile' + statementItem.id"
                        title="Conciliar mesmo assim" size="small" type="primary" :clicked="onForceReconcile" />
                    <Button :_key="'btnDesfazer' + statementItem.id" title="Desfazer" size="small" type="danger"
                        :clicked="onDesfazer" />
                    <div v-if="hasRemainingBalance && !condensed">
                        <Button :_key="'btnLinkMore' + recordItem.id" title="Vincular mais um item" size="small"
                            type="edit" :clicked="onLinkMore" />
                    </div>
                </div>
            </template>


        </template>

        <template v-else-if="isReconciled">
            <div class="reconciliation-match-card-created-label">
                <i class="fa-solid fa-circle-check"></i>
                Lançamento criado a partir do extrato
            </div>
            <div class="reconciliation-match-card-body">
                <Badge title="Conciliado" type="success" size="small" />
            </div>
        </template>
    </div>
</template>
<script>

import Button from "@/ui/forms/Button";
import Badge from "@/ui/layout/Badge";

import reconciliationRecordMixin from "../../mixins/reconciliationRecord.mixin.js";

export default {
    name: "ReconciliationMatchCard",
    mixins: [reconciliationRecordMixin],
    components: { Button, Badge },
    props: {
        statementItem: Object,
        recordItem: { type: Object, default: null },
        condensed: { type: Boolean, default: false },
    },
    computed: {
        isPending() {
            return this.statementItem.status == 'pendente';
        },
        isSuggested() {
            return this.statementItem.status == 'sugerido';
        },
        isDivergent() {
            return this.statementItem.status == 'divergente';
        },
        isReconciled() {
            return this.statementItem.status == 'conciliado';
        },
        hasRemainingBalance() {
            if (!this.recordItem) return false;
            return this.recordItem.remainingValue > this.valueTolerance;
        },
        cardClasses() {
            return {
                'reconciliation-match-card-empty-state': this.isPending,
                'reconciliation-match-card-suggested': this.isSuggested,
                'reconciliation-match-card-divergent': this.isDivergent,
                'reconciliation-match-card-reconciled': this.isReconciled,
            };
        },
    },
    methods: {
        onSearch() {
            this.$emit('search', this.statementItem);
        },
        onCreate() {
            this.$emit('create', this.statementItem);
        },
        onConciliar() {
            this.$emit('conciliar', this.statementItem);
        },
        onDesfazer() {
            this.$emit('desfazer', this.statementItem);
        },
        onForceReconcile() {
            this.$emit('force-reconcile', this.statementItem);
        },
        onLinkMore() {
            this.$emit('link-more', this.recordItem);
        },
    },
};
</script>
<style scoped>
.reconciliation-match-card {
    border: 1px solid var(--border-color);
    border-radius: 10px;
    padding: 12px 14px;
    background: var(--bg-base);
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    box-sizing: border-box;
    width: 100%;
}

.reconciliation-match-card-empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border-style: dashed;
}

.reconciliation-match-card-suggested {
    border-left: 3px solid var(--color-brand);
}

.reconciliation-match-card-divergent {
    box-shadow: 0 0 0 1px var(--color-danger);
}

.reconciliation-match-card-reconciled {
    background: var(--bg-elevated);
    border-left: 3px solid var(--color-success);
}

.reconciliation-match-card-empty {
    display: flex;
    gap: 8px;
}

.reconciliation-row-icon-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: var(--bg-elevated);
    color: var(--text-secondary);
    cursor: pointer;
    font-size: 13px;
    transition: background 0.15s, color 0.15s;
}

.reconciliation-row-icon-btn:hover {
    background: var(--bg-hover);
    color: var(--color-brand);
}

.reconciliation-match-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.reconciliation-match-card-value {
    font-weight: 700;
    font-size: 16px;
    color: var(--text-primary);
}

.reconciliation-match-card-value-positive {
    color: var(--color-success);
}

.reconciliation-match-card-value-negative {
    color: var(--color-danger);
}

.reconciliation-match-card-count {
    font-size: 12px;
    color: var(--text-secondary);
}

.reconciliation-match-card-body {
    margin-top: 6px;
}

.reconciliation-match-card-description {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
}

.reconciliation-match-card-subtext {
    font-size: 12px;
    color: var(--text-secondary);
    margin-top: 2px;
}

.reconciliation-match-card-condensed-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--text-secondary);
}

.reconciliation-match-card-existing-balance {
    font-size: 11.5px;
    color: var(--color-warning);
    margin-top: 4px;
}

.reconciliation-match-card-paired-info {
    font-size: 11.5px;
    color: var(--color-brand);
    margin-top: 4px;
}

.reconciliation-match-card-confidence {
    font-size: 11.5px;
    color: var(--color-brand);
    font-weight: 600;
    margin-top: 5px;
}

.reconciliation-match-card-diff {
    font-size: 12px;
    color: var(--color-danger);
    margin-top: 6px;
}

.reconciliation-match-card-actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;
}

.reconciliation-match-card-created-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-success);
}
</style>
